import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Names what failed, so the message can say which part is gone. */
  area: string;
}

interface State {
  error: Error | null;
}

/**
 * Stops one broken component from taking down the whole NOC.
 *
 * This exists because it happened. A controller returned `{ data: <Promise> }`,
 * which serialised to `{"data":{}}`, and the client's `data.map` threw. React
 * unmounted the entire tree: no navigation, no alarm counters, no dashboard —
 * a blank white page, on a screen whose entire job is to be watched during an
 * outage. The API bug was trivial. Losing the whole application to it was not.
 *
 * Boundaries are placed per route and around the topbar counters, so a failure
 * in one module leaves the rest usable and says plainly what is missing. The
 * alternative — a single boundary at the root — turns every component fault
 * back into a blank screen with nicer wording.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Logged verbatim. A NOC operator reporting "the alarms panel is broken"
    // is much easier to act on with the actual stack in the browser console.
    console.error(`[${this.props.area}] render failed`, error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className="m-4 rounded-lg bg-surface p-4 shadow-1" role="alert">
        <p className="text-sm font-semibold" style={{ color: 'var(--sev-critical)' }}>
          {this.props.area} could not be displayed.
        </p>
        <p className="mt-1 text-xs text-secondary">
          This is a fault in the OSS itself, not in the network. Other pages still work — but
          anything this panel would have shown is currently invisible, so do not read its absence
          as "nothing is wrong".
        </p>
        <p className="mt-2 font-mono text-xs text-muted">{this.state.error.message}</p>
        <button
          onClick={() => this.setState({ error: null })}
          className="mt-3 rounded-md border border-border px-3 py-1.5 text-xs text-secondary hover:bg-hover"
        >
          Try again
        </button>
      </div>
    );
  }
}
